import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import xlsx from "json-as-xlsx";
import { USER, XLSX } from "../../config/constants";
import socket, { APP_UPDATE_AVAILABLE } from "../../socket";
import { getFieldErrors, IFieldError, interpretDataIntoXlsxFormat, QueryStringBuilder } from "../helpers";
import { IEnumCustomer } from "./customersReducer";
import { tryConfirmLogin } from "./authReducer";


export interface IEnumPayment {
    _id: string;
    ref: string;
    type: string;
    customerId: string;
    resourceType: string;
    resourceId: string;
    amount: number;
    status: string;
    isReplacement?: boolean;
    createdAt: string;
    updatedAt: string;
}

export interface IPayment {
    _id: string;
    ref: string;
    type: string;
    customer: IEnumCustomer;
    resourceType: string;
    resourceId: string;
    amount: number;
    status: string;
    isReplacement?: boolean;
    createdAt: string;
    updatedAt: string;

    bankName?: string;
    accountName?: string;
    accountNumber?: string;
    referenceNumber?: string;

    sessionId?: string;
}

class ConfirmPaymentErrs {
    form: string | null = null;
    refNumber: string | null = null;
}

class ExportPaymentsHistoryErrs {
    form: string | null = null;
    startTimeStamp: string | null = null;
    endTimeStamp: string | null = null;
}

export interface IPaymentsState {
    confirmingPayment: boolean;
    confirmPaymentErrs: ConfirmPaymentErrs;
    paymentConfirmed: boolean;
    fetchingRecentPayments: boolean;
    fetchRecentPaymentsErr: string | null;
    recentPayments: IEnumPayment[];
    fetchingMainPayment: boolean;
    fetchMainPaymentErr: string | null;
    mainPayment: IPayment | null;
    exportingPaymentsHistory: boolean | null;
    exportedPaymentsHistory: boolean | null;
    exportPaymentsHistoryErrs: ExportPaymentsHistoryErrs;
}

const initialState = {
    paymentConfirmed: false,
    confirmingPayment: false,
    confirmPaymentErrs: new ConfirmPaymentErrs(),
    fetchingRecentPayments: false,
    fetchRecentPaymentsErr: null,
    recentPayments: [],
    fetchingMainPayment: false,
    fetchMainPaymentErr: null,
    mainPayment: null,
    exportingPaymentsHistory: false,
    exportedPaymentsHistory: false,
    exportPaymentsHistoryErrs: new ExportPaymentsHistoryErrs()
} as IPaymentsState;

const paymentsSlice = createSlice({
    name: 'payments',
    initialState,
    reducers: {
        clearConfirmPaymentFieldErr: (state: IPaymentsState, action: PayloadAction<string>) => {
            state.confirmPaymentErrs[action.payload as keyof ConfirmPaymentErrs] = null;
        },
        clearConfirmPaymentErrs: (state: IPaymentsState) => {
            state.confirmPaymentErrs = new ConfirmPaymentErrs();
        },
        tryResetConfirmPaymentFormErr: (state: IPaymentsState) => {
            let shouldReset = true;

            for (let key in state.confirmPaymentErrs) {

                if (key === 'form') {
                    continue;
                }

                if (state.confirmPaymentErrs[key as keyof ConfirmPaymentErrs]) {
                    shouldReset = false;
                    break;
                }
            }

            if (shouldReset) {
                state.confirmPaymentErrs.form = null;
            }
        },
        confirmPaymentStart: (state: IPaymentsState) => {
            state.confirmingPayment = true;
        },

        confirmPaymentPass: (state: IPaymentsState) => {
            state.confirmingPayment = false;
            state.paymentConfirmed = true;
        },
        resetPaymentConfirmed: (state: IPaymentsState) => {
            state.paymentConfirmed = false;
        },
        confirmPaymentFail: (state: IPaymentsState, action: PayloadAction<IFieldError[]>) => {
            state.confirmingPayment = false;


            const errs = action.payload;

            console.log(errs);

            for (const err of errs) {
                for (const key in state.confirmPaymentErrs) {
                    if (err.field === key) {
                        state.confirmPaymentErrs[key as keyof ConfirmPaymentErrs] = err.msg;
                        break;
                    }
                }
            }


            if (!state.confirmPaymentErrs.form) {
                state.confirmPaymentErrs.form = 'Form incorrectly filled.';
            }
        },
        clearFetchRecentPaymentsErr: (state: IPaymentsState) => {
            state.fetchRecentPaymentsErr = null;
        },
        fetchRecentPaymentsStart: (state: IPaymentsState) => {
            state.fetchingRecentPayments = true;
            state.fetchRecentPaymentsErr = null;
        },
        fetchRecentPaymentsPass: (state: IPaymentsState, action: PayloadAction<IEnumPayment[]>) => {
            state.fetchingRecentPayments = false;
            state.recentPayments = action.payload;
        },
        fetchRecentPaymentsFail: (state: IPaymentsState, action: PayloadAction<string>) => {
            state.fetchingRecentPayments = false;
            state.fetchRecentPaymentsErr = action.payload;
        },
        clearFetchMainPaymentErr: (state: IPaymentsState) => {
            state.fetchMainPaymentErr = null;
        },
        fetchMainPaymentStart: (state: IPaymentsState) => {
            state.mainPayment = null;
            state.fetchingMainPayment = true;
            state.fetchMainPaymentErr = null;
        },
        fetchMainPaymentPass: (state: IPaymentsState, action: PayloadAction<IPayment>) => {
            state.fetchingMainPayment = false;
            state.mainPayment = action.payload;
        },
        fetchMainPaymentFail: (state: IPaymentsState, action: PayloadAction<string>) => {
            state.fetchingMainPayment = false;
            state.fetchMainPaymentErr = action.payload;
        },
        setMainPayment: (state: IPaymentsState, action: PayloadAction<IPayment | null>) => {
            state.mainPayment = action.payload;
        },
        clearExportPaymentsHistoryFieldErr: (state: IPaymentsState, action: PayloadAction<string>) => {
            state.exportPaymentsHistoryErrs[action.payload as keyof ExportPaymentsHistoryErrs] = null;
        },
        clearExportPaymentsHistoryErrs: (state: IPaymentsState) => {
            state.exportPaymentsHistoryErrs = new ExportPaymentsHistoryErrs();
        },
        tryResetExportPaymentsHistoryFormErr: (state: IPaymentsState) => {
            let shouldReset = true;

            for (let key in state.exportPaymentsHistoryErrs) {

                if (key === 'form') {
                    continue;
                }

                if (state.exportPaymentsHistoryErrs[key as keyof ExportPaymentsHistoryErrs]) {
                    shouldReset = false;
                    break;
                }
            }

            if (shouldReset) {
                state.exportPaymentsHistoryErrs.form = null;
            }
        },
        resetExportedExportPaymentsHistory: (state: IPaymentsState) => {
            state.exportedPaymentsHistory = false;
        },
        exportPaymentsHistoryStart: (state: IPaymentsState) => {
            state.exportingPaymentsHistory = true;
            state.exportedPaymentsHistory = false;
        },
        exportPaymentsHistoryPass: (state: IPaymentsState) => {
            state.exportingPaymentsHistory = false;
            state.exportedPaymentsHistory = true;
        },
        exportPaymentsHistoryFail: (state: IPaymentsState, action: PayloadAction<IFieldError[]>) => {
            state.exportingPaymentsHistory = false;


            const errs = action.payload;

            for (const err of errs) {
                for (const key in state.exportPaymentsHistoryErrs) {
                    if (err.field === key) {
                        state.exportPaymentsHistoryErrs[key as keyof ExportPaymentsHistoryErrs] = err.msg;
                        break;
                    }
                }
            }


            if (!state.exportPaymentsHistoryErrs.form) {
                state.exportPaymentsHistoryErrs.form = 'Form incorrectly filled.';
            }
        },
    }
});

export const {
    clearConfirmPaymentFieldErr,
    clearConfirmPaymentErrs,
    tryResetConfirmPaymentFormErr,
    resetPaymentConfirmed,
    confirmPaymentStart,
    confirmPaymentPass,
    confirmPaymentFail,
    clearFetchRecentPaymentsErr,
    fetchRecentPaymentsStart,
    fetchRecentPaymentsPass,
    fetchRecentPaymentsFail,
    clearFetchMainPaymentErr,
    fetchMainPaymentStart,
    fetchMainPaymentPass,
    fetchMainPaymentFail,
    setMainPayment,
    clearExportPaymentsHistoryFieldErr,
    clearExportPaymentsHistoryErrs,
    tryResetExportPaymentsHistoryFormErr,
    resetExportedExportPaymentsHistory,
    exportPaymentsHistoryStart,
    exportPaymentsHistoryPass,
    exportPaymentsHistoryFail
} = paymentsSlice.actions;

interface ITryConfirmPaymentArg {
    refNumber: string;
    authToken: string;
    setConfirmed: (truth: boolean) => void;
    paymentId: string;
}

export const tryConfirmPayment = createAsyncThunk('orders/tryConfirmPayment', (arg: ITryConfirmPaymentArg, { dispatch }) => {

    const { refNumber, authToken, setConfirmed, paymentId } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/payments/${paymentId}/confirm`;


    dispatch(confirmPaymentStart());

    axios.post(endpoint, { refNumber }, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
socket.emit(APP_UPDATE_AVAILABLE, USER);
        dispatch(confirmPaymentPass());
        setConfirmed(true);
    }).catch(err => {

        const data = err?.response?.data;


        let fieldErrors: IFieldError[] = [];

        if (data?.errors) {
            fieldErrors = getFieldErrors(data.errors);
        } else if (data?.err) {
            fieldErrors = [{ field: 'form', msg: data.err }];
        } else {
            fieldErrors = [{ field: 'form', msg: 'Check your network connection.' }];
        }

        dispatch(confirmPaymentFail(fieldErrors));
    })
});

interface ITryFetchRecentPaymentsArg {
    authToken: string;
    customerId?: string;
    status?: string;
    orderId?: string;
}

export const tryFetchRecentPayments = createAsyncThunk('payments/tryFetchRecentPayments', (arg: ITryFetchRecentPaymentsArg, { dispatch }) => {
    const { authToken, customerId, status, orderId } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/payments${customerId || status || orderId ? '?' : ''}${customerId ? `customerId=${customerId}` : ''}${customerId && orderId ? '&' : ''}${orderId ? `orderId=${orderId}` : ''}${(customerId || orderId) && status ? '&' : ''}${status ? `status=${status}` : ''}`;

    dispatch(fetchRecentPaymentsStart());
    axios.get(endpoint, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        dispatch(fetchRecentPaymentsPass(res.data));
    }).catch(err => {
        const data = err?.response?.data;

        if (data?.err && (data.err === "Session expired")) {
            dispatch(tryConfirmLogin({authToken}));
            return;
        }

        if (data?.err) {
            dispatch(fetchRecentPaymentsFail(data.err));
        } else {
            dispatch(fetchRecentPaymentsFail('Check your network connection.'));
        }
    })
});

interface ITryFetchMainPaymentArg {
    authToken?: string;
    PaymentId: string;
}

export const tryFetchMainPayment = createAsyncThunk('payments/tryFetchMainPayment', (arg: ITryFetchMainPaymentArg, { dispatch }) => {
    const { authToken, PaymentId } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/payments/${PaymentId}`;

    dispatch(fetchMainPaymentStart());
    axios.get(endpoint, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken ?? ''}`
        }
    }).then(res => {

        dispatch(fetchMainPaymentPass(res.data));
    }).catch(err => {
        const data = err?.response?.data;

        if (data?.err && (data.err === "Session expired")) {
            dispatch(tryConfirmLogin({authToken: authToken ?? ""}));
            return;
        }

        if (data?.err) {
            dispatch(fetchMainPaymentFail(data.err));
        } else {
            dispatch(fetchMainPaymentFail('Check your network connection.'));
        }
    })
});

export interface IData4TryExportPaymentsHistory {
    startTimeStamp?: string;
    endTimeStamp?: string;
    customerId?: string;
    resourceId?: string;
}

interface ITryExportPaymentsHistoryArg {
    format: string;
    authToken: string;
    data: IData4TryExportPaymentsHistory;
}

export const tryExportPaymentsHistory = createAsyncThunk("payments/tryExportPaymentsHistory", (arg: ITryExportPaymentsHistoryArg, { dispatch }) => {

    const { authToken, format, data } = arg;

    const queryBuilder = new QueryStringBuilder();

    for (const key in data) {
        if ((data[key as keyof typeof data] !== undefined) && (data[key as keyof typeof data] !== "")) {
            queryBuilder.addInput(key, data[key as keyof typeof data] as string);
        }
    }

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/payments-history${queryBuilder.getString()}`;

    dispatch(exportPaymentsHistoryStart());
    axios.get(endpoint, {
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${authToken}`
        }
    }).then(res => {
        const data = res.data;

        if (data.length === 0) {
            const fieldErrors = [{ field: 'form', msg: 'Export Empty.' }];

            dispatch(exportPaymentsHistoryFail(fieldErrors));
        } else {

            switch (format) {
                case XLSX:
                default:
                    const fileSettings = {
                        fileName: "payments_history"        //TODO: dynamically generate the name, based on the parameters used to fetch the data
                    };

                    try {
                        xlsx(interpretDataIntoXlsxFormat(data), fileSettings);
                    } catch (err) {
                            alert("err");
                        console.log(err);
                    }
                    break;
            }

            dispatch(exportPaymentsHistoryPass());
        }
    }).catch(err => {
        const data = err?.response?.data;

        if (data?.err && (data.err === "Session expired")) {
            dispatch(tryConfirmLogin({authToken}));
            return;
        }

        let fieldErrors: IFieldError[] = [];

        if (data?.errors) {
            fieldErrors = getFieldErrors(data.errors);
        } else if (data?.err) {
            fieldErrors = [{ field: 'form', msg: data.err }];
        } else {
            console.log(data);
            fieldErrors = [{ field: 'form', msg: 'Check your network connection.' }];
        }

        dispatch(exportPaymentsHistoryFail(fieldErrors));
    })


});

export default paymentsSlice.reducer;